import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import adaEmbed from '@ada-support/embed2';
import { RootState, store } from 'app/store';
import type { LooseObjectType } from 'utils/globalTypes';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { getFromBrandConfig } from 'brand-config';
import { ENV } from 'utils/constants';
import { logErrors, isValidNonEmptyObj } from 'utils';
import {
  selectBrandCode,
  selectIsBrandDetailsExist,
} from 'services/brand/brandSelectors';
import { selectUserSignInStatus } from 'services/session/sessionSelectors';
import { fetchThinSessionDetails } from 'services/session/sessionAsync';
import { selectOfferPlanCode } from 'services/offer/offerSelectors';
import { selectUserId } from 'services/user/userSelectors';

type WindowInfo = {
  hasActiveChatter: boolean;
  hasClosedChat: boolean;
  isChatOpen: boolean;
  isDrawerOpen: boolean;
};

type AdaChatProviderProps = {
  children: ReactNode;
};

const ADA_HANDLE = ENV === 'prod' ? 'pango-gr' : 'generative-sandbox-pango-ir';

// Embed is a page singleton; a second start() rejects (StrictMode, availability re-flip).
let isAdaStarted = false;

const AdaChatContext = createContext({
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  openAdaChat(campaignName: string, payload?: LooseObjectType) {},
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  setAdaChatMetaFields(payload?: LooseObjectType) {},
  isAdaChatAvailable: false,
});

function AdaChatProvider({ children }: AdaChatProviderProps) {
  const dispatch = useAppDispatch();
  const isBrandDetailsExist = useAppSelector(selectIsBrandDetailsExist);

  /* Not providing ADA Chat feature for eyeon users */
  const isAdaChatAvailable = useMemo(() => {
    if (!isBrandDetailsExist) {
      return false;
    }

    return getFromBrandConfig('adaChat');
  }, [isBrandDetailsExist]);

  const showAdaChat = useCallback((action: boolean) => {
    adaEmbed.getInfo().then((windowInfo: WindowInfo) => {
      if (windowInfo.isDrawerOpen !== action) {
        adaEmbed.toggle();
      }
    });
  }, []);

  /* Function will trigger when the Ada is ready */
  const handleAdaReady = useCallback(() => {
    adaEmbed
      .closeCampaign()
      .catch(logErrors)
      .finally(() => showAdaChat(false));
  }, [showAdaChat]);

  /* Send meta fields and sensitive meta fields to the ADA on chat drawer window opened */
  const handleToggle = useCallback((isDrawerOpen: boolean) => {
    if (isDrawerOpen) {
      const appStore = store.getState() as RootState;
      const brandCode = selectBrandCode(appStore);
      const planCode = selectOfferPlanCode(appStore);
      const isLogin = selectUserSignInStatus(appStore);
      const userId = selectUserId(appStore);

      const metaFields = {
        brand: brandCode,
        planCode: planCode ?? null,
        pageName: window.location.pathname,
        // Omit when signed out so a deep-link id isn't overwritten.
        ...(userId ? { Ada_User_ID_Captured: userId } : {}),
      };

      adaEmbed.setMetaFields(metaFields);

      if (isLogin && ENV !== 'prod') {
        dispatch(fetchThinSessionDetails())
          .then((response) => {
            if (response?.accessToken) {
              adaEmbed.setSensitiveMetaFields({
                Ada_JWT_Token_Captured: response.accessToken,
              });
              adaEmbed.setMetaFields({
                Ada_JWT_Token_Captured_Test: response.accessToken,
              });
            }
          })
          .catch((error) => {
            logErrors(error);
          });
      }
    }
  }, []);

  useEffect(() => {
    if (isAdaChatAvailable && !isAdaStarted) {
      isAdaStarted = true;
      adaEmbed
        .start({
          handle: ADA_HANDLE,
          sensitiveMetaFields: {
            Ada_JWT_Token_Captured: '',
          },
          metaFields: {
            Ada_JWT_Token_Captured_Test: '',
          },
          adaReadyCallback: handleAdaReady,
          toggleCallback: handleToggle,
        })
        .catch(logErrors);
    }
  }, [isAdaChatAvailable, handleAdaReady, handleToggle]);

  const openAdaChat = useCallback(
    (campaignName: string, payload?: LooseObjectType) => {
      if (isAdaChatAvailable) {
        if (isValidNonEmptyObj(payload)) {
          adaEmbed.setMetaFields({
            ...(payload ?? {}),
          });
        }

        adaEmbed.triggerCampaign(campaignName, {
          ignoreStatus: false,
          ignoreFrequency: false,
        });

        showAdaChat(true);
      }
    },
    [isAdaChatAvailable],
  );

  // Angular parity — `app.component.ts:291-303` (`showAdaChat()`).
  // Email deep-links land on URLs like
  // `/single-alert/:alertId?id=…&emailAddress=…&firstName=…&lastName=…&alertCode=…`
  // (Ada's `sign-in` campaign expects those fields as meta so the
  // conversation opens pre-personalised for the unauthed member).
  // Captured ONCE via `useState` lazy init before `AuthRoute` can
  // rewrite the URL to `/sign-in` (which strips the path but keeps
  // the query — either way, this reads the original entry URL).
  const [deepLinkChatParams] = useState<URLSearchParams>(
    () =>
      new URLSearchParams(
        typeof window !== 'undefined' ? window.location.search : '',
      ),
  );
  const deepLinkChatFiredRef = useRef(false);
  useEffect(() => {
    if (!isAdaChatAvailable) return;
    if (deepLinkChatFiredRef.current) return;
    // Gate matches Angular: both `id` and `emailAddress` must be
    // present. Everything else is optional context passed through
    // to the campaign.
    const id = deepLinkChatParams.get('id');
    const emailAddress = deepLinkChatParams.get('emailAddress');
    if (!id || !emailAddress) return;
    deepLinkChatFiredRef.current = true;
    openAdaChat('sign-in', {
      Ada_User_ID_Captured: id,
      emailName: deepLinkChatParams.get('emailName') ?? '',
      firstName: deepLinkChatParams.get('firstName') ?? '',
      lastName: deepLinkChatParams.get('lastName') ?? '',
      emailAddress,
      alertCode: deepLinkChatParams.get('alertCode') ?? '',
    });
  }, [isAdaChatAvailable, deepLinkChatParams, openAdaChat]);

  const setAdaChatMetaFields = useCallback(
    (payload: LooseObjectType) => {
      if (isAdaChatAvailable) {
        adaEmbed.setMetaFields({
          ...(payload ?? {}),
        });
      }
    },
    [isAdaChatAvailable],
  );

  const value = useMemo(
    () => ({
      openAdaChat,
      setAdaChatMetaFields,
      isAdaChatAvailable,
    }),
    [isAdaChatAvailable, openAdaChat, setAdaChatMetaFields],
  );

  return (
    <AdaChatContext.Provider value={value}>{children}</AdaChatContext.Provider>
  );
}

export const useAdaChat = () => useContext(AdaChatContext);

export default AdaChatProvider;
